/* ==========================================================================
   DH CART DRAWER — behaviour
   Daisy & Hen · Horizon 4.1.4

   REBUILT 2026-09-06: the previous design mutated the cart, dispatched a
   CartLinesUpdateEvent, and depended on Horizon's native cart-items-component
   (listening globally on document) to catch it and re-render our section for
   us. That indirection is exactly why failures were intermittent and silent
   — three rounds of screen recordings couldn't pin it down because success
   depended on a chain of timing/behaviour in code we don't control. Horizon's
   OWN native code doesn't work that way: its quantity-stepper handler fetches,
   gets the section HTML back, and calls morphSection() on it directly, in the
   same function. This file now does the same: every mutation here owns its
   own render, deterministically, with a bounded timeout and a visible error
   if something genuinely fails — no dependency on a separate listener ever
   catching anything, and no blind setTimeout delays guessing when a morph
   might be done.

   Handles: accordion toggles (promo tiers / gift message, mutually exclusive),
   promo gift add·change·remove, greeting-card add·remove, debounced gift-message
   save to a cart attribute, auto-removal of an ineligible selected gift, and
   required-gift-message checkout validation.
   ========================================================================== */

import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

(() => {
  'use strict';

  const SEL = {
    drawer: 'theme-drawer#cart-drawer',
    promo: '[data-dh-promo-gift]',
    promoToggle: '[data-dh-promo-gift-toggle]',
    promoPanel: '[data-dh-promo-gift-panel]',
    promoIcon: '[data-dh-promo-gift-icon]',
    promoBtn: '[data-dh-promo-gift-add]',
    promoVariantSelect: '[data-dh-promo-gift-variant-select]',
    promoRemove: '[data-dh-promo-gift-remove], .dh-cart-promo-gifts__added',
    gift: '[data-dh-gift-message]',
    giftToggle: '[data-dh-gift-message-toggle]',
    giftPanel: '[data-dh-gift-message-panel]',
    giftIcon: '[data-dh-gift-message-icon]',
    giftInput: '[data-dh-gift-message-input]',
    giftCounter: '[data-dh-gift-message-counter]',
    giftStatus: '[data-dh-gift-message-status]',
    giftAdd: '[data-dh-gift-add-on-add]',
    giftRemove: '[data-dh-gift-add-on-remove], .dh-cart-gift-message__add-on-added',
    checkout: '[name="checkout"]',
  };

  // FIX (2026-09-06): `Theme` is declared with `const Theme = {...}` in a
  // classic script (snippets/scripts.liquid). Top-level const/let bindings
  // never attach to `window`, so `window.Theme` was always undefined here —
  // every other file that reads these routes (cart-discount.js, native
  // component-cart-items.js) references the bare global `Theme` instead.
  // That bug silently dropped every route to its hardcoded fallback, e.g.
  // ADD_URL becoming '/cart/add' instead of '/cart/add.js', which made the
  // gift-card and promo-gift "Add" actions fail silently (stuck on
  // "Adding…", then revert, with no cart change).
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const ADD_URL = routes.cart_add_url || '/cart/add.js';
  const CHANGE_URL = routes.cart_change_url || '/cart/change';
  const UPDATE_URL = routes.cart_update_url || '/cart/update';

  // Accordion open-state survives the native morph: keyed by block id.
  let saveTimer = null;
  let cleanupScheduled = false;

  // FIX (2026-09-06): cleanupInvalidGift() (auto-removes a stale/ineligible
  // gift on load or after any cart change) and the manual promo/card
  // add·change·remove handlers below were completely uncoordinated — both
  // read the cart via fetchCart() and both mutate it, with no locking. If a
  // shopper tapped "Add" while a cleanup for that same line was in flight,
  // the manual action's fetchCart() snapshot still showed the (about to be
  // deleted) old line, matched on variant id, concluded "already selected",
  // and returned without adding anything — the cleanup's own delete then
  // repainted the section back to a plain "Add" state, so it looked like
  // the click had simply been swallowed. Routing every mutation through one
  // queue guarantees they run strictly one-at-a-time, so fetchCart() inside
  // any handler always reflects a fully-settled cart.
  let mutationChain = Promise.resolve();
  function enqueueMutation(task) {
    const run = mutationChain.then(task, task);
    mutationChain = run.catch(() => {});
    return run;
  }

  const drawerEl = () => document.querySelector(SEL.drawer);

  // FIX (2026-09-06): every catch block below silently discarded the real
  // error — no console output, nothing — which is why earlier rounds of
  // screen recordings couldn't pin down the promo-gift add failure: there
  // was never anything to look at. This logs the actual response/description
  // so a failed add is diagnosable straight from the console.
  function reportMutationError(context, error) {
    const detail = (error && (error.description || error.message)) || error;
    console.error(`[dh-cart-drawer] ${context} failed:`, detail);
  }

  // Also surface it ON the control itself, briefly — so a failure is visible
  // without opening devtools at all, not just logged.
  const ERROR_FLASH_MS = 2200;
  function flashError(el, fallbackLabel) {
    if (!el) return;
    el.textContent = 'Error — tap to retry';
    el.classList.add('dh-cart-drawer__action-error');
    window.setTimeout(() => {
      if (!el.isConnected) return;
      el.textContent = fallbackLabel || 'Add';
      el.classList.remove('dh-cart-drawer__action-error');
    }, ERROR_FLASH_MS);
  }

  // A hung request used to leave a button reading "Adding…" indefinitely (or
  // "succeed" so late the drawer looked like it needed a reopen to catch up).
  // Bound every mutation so a stalled network fails predictably and fast.
  const MUTATE_TIMEOUT_MS = 12000;

  // "Add → Adding… → Added → Remove": addLine() resolving successfully only
  // gets us to the native "Remove" state instantly via the section morph —
  // there's no visible "Added" beat in between. These track which variant
  // was JUST added by this client so afterRender()/hydrateAddedCards() can
  // hold on "Added" for a moment before flipping to "Remove", without
  // affecting rows that were already in the cart from a previous render.
  const ADDED_FLASH_MS = 900;
  const recentlyAddedCardVariants = new Map();
  const recentlyAddedPromoVariants = new Map();

  function sectionId() {
    const el =
      document.querySelector(`${SEL.drawer} cart-items-component[data-section-id]`) ||
      document.querySelector(`${SEL.drawer} [data-dh-section-id]`);
    return el ? el.getAttribute('data-section-id') || el.getAttribute('data-dh-section-id') || '' : '';
  }

  /* ---- Cart mutation → we render it ourselves --------------------------- */

  async function mutate(url, body, meta) {
    const id = sectionId();
    const payload = Object.assign({}, body);
    if (id) {
      // Shopify's cart endpoints expect `sections` as a comma-separated
      // STRING, not a JSON array — see cart-items-component's own
      // updateQuantity(), which does `Array.from(...).join(',')`.
      payload.sections = id;
      payload.sections_url = window.location.pathname;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), MUTATE_TIMEOUT_MS);

    let json;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      json = await res.json();
      if (!res.ok) {
        throw new Error(json && json.description ? json.description : `Cart request failed (${res.status})`);
      }
    } catch (error) {
      if (error && error.name === 'AbortError') {
        throw new Error('Request timed out — check your connection and try again.');
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }

    // Render it ourselves, directly, right here — the same pattern Horizon's
    // own native quantity-stepper handler uses. No dependency on a separate
    // listener elsewhere catching an event and deciding to act on it.
    //
    // FIX (2026-09-06): 'hydration' mode is what native quantity-stepper
    // updates use for the drawer, because it's built to preserve things like
    // focus and in-progress input values across a re-render. Confirmed live
    // (via direct DOM inspection, not just screenshots) that on our buttons
    // it updates attributes correctly — aria-label flipped to "Remove free
    // gift…" right after a successful add — but leaves the visible text
    // content stale ("Add" stayed on screen), because the button stayed the
    // focused element and the morph preserves a focused element's text
    // regardless of mode. Switching to 'full' mode alone didn't fully solve
    // this for the promo gift button specifically — see the structural fix
    // in handlePromoAction/hydratePromoAdded below, which sidesteps it the
    // same way the greeting card always has. 'full' mode is still the
    // correct choice here regardless, since our elements aren't inputs and
    // have no focus/typing state worth preserving.
    //
    // Also: this render step used to be inside the same try/catch as the
    // network request, so if it threw for any reason it looked identical to
    // the ADD/REMOVE itself having failed — flashError() fired "Error — tap
    // to retry" even though the cart mutation had already genuinely
    // succeeded. A rendering hiccup must never be reported to the shopper as
    // their action failing. If our own render throws, fall back to a plain
    // re-fetch of the section; if even that fails, swallow it silently —
    // worst case the drawer looks stale until the next change, but we never
    // lie about the mutation itself, which we already know succeeded by
    // this point.
    if (id) {
      try {
        const html = json.sections && json.sections[id];
        if (html) {
          await morphSection(id, html, { mode: 'full' });
        } else {
          await sectionRenderer.renderSection(id, { cache: false, mode: 'full' });
        }
        afterRender();
      } catch (renderError) {
        console.warn('[dh-cart-drawer] cart updated successfully but re-rendering the drawer failed:', renderError);
        try {
          await sectionRenderer.renderSection(id, { cache: false, mode: 'full' });
          afterRender();
        } catch (fallbackError) {
          console.warn('[dh-cart-drawer] fallback re-render also failed; drawer may look stale until the next change:', fallbackError);
        }
      }
    }

    // Still tell the rest of the theme (header cart count/bubble, apps) —
    // but our own UI no longer depends on anyone receiving this correctly.
    // Tagged so OUR OWN listener below (for externally-triggered changes)
    // skips it — we already rendered and called afterRender() ourselves,
    // right above. Without this tag, afterRender() ran a second time for
    // every one of our own actions, and running hydratePromoAdded()/
    // hydrateAddedCards() twice in close succession raced their own "Added"
    // flash timers against each other: the second pass would read back the
    // first pass's mid-flash "Added" text as the label to restore to,
    // occasionally leaving a just-added gift showing the wrong state.
    const target = drawerEl() || document;
    const ownEvent = new CartLinesUpdateEvent({
      action: meta.action,
      context: meta.context || 'cart',
      lines: meta.lines || [],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
        detail: {
          sections: json.sections,
          items: json.items,
          itemCount: json.item_count,
          source: 'dh-cart-drawer',
          didError: false,
        },
      }),
    });
    ownEvent.__dhOwnMutation = true;
    target.dispatchEvent(ownEvent);

    return json;
  }

  const addLine = (variantId, properties) =>
    mutate(
      ADD_URL,
      { items: [{ id: Number(variantId), quantity: 1, properties: properties || {} }] },
      { action: 'add', context: 'cart', lines: [{ merchandiseId: String(variantId), quantity: 1 }] }
    );

  const changeLine = (lineKey, quantity) =>
    mutate(
      CHANGE_URL,
      { id: lineKey, quantity: Number(quantity) },
      { action: Number(quantity) > 0 ? 'update' : 'remove', context: 'cart', lines: [{ id: lineKey, quantity: Number(quantity) }] }
    );

  async function fetchCart() {
    const res = await fetch(`${routes.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return res.json();
  }

  function lineProp(properties, name) {
    if (!properties) return '';
    if (Array.isArray(properties)) {
      const match = properties.find((p) => p && p[0] === name);
      return match ? match[1] : '';
    }
    return properties[name] || '';
  }
  /* ---- Native <details> open-persistence across cart re-renders --------- */
  /* The gift/promo rows are native <details> (accordion-custom-component owns
     the toggle). On a cart change the section re-renders closed, so we remember
     which rows the shopper opened and re-open them after the morph. */

  const openDetails = new Set();

  function trackDetail(details) {
    const id = details && details.getAttribute('data-dh-details-id');
    if (!id) return;
    if (details.open) openDetails.add(id);
    else openDetails.delete(id);
  }

  function markDetailOpen(el) {
    const details = el && el.closest('details[data-dh-details-id]');
    if (details) openDetails.add(details.getAttribute('data-dh-details-id'));
  }

  function reopenDetails() {
    const d = drawerEl();
    if (!d) return;
    d.querySelectorAll('details[data-dh-details-id]').forEach((details) => {
      const id = details.getAttribute('data-dh-details-id');
      if (openDetails.has(id) && !details.open) details.open = true;
    });
  }

  /* ---- Cart message rotator (native fade + native caret chevrons) ------- */

  function setArrowColour(root, slide) {
    const fg = slide && slide.getAttribute('data-dh-fg');
    if (fg) root.style.setProperty('--dh-cart-announce-arrow-color', fg);
    else root.style.removeProperty('--dh-cart-announce-arrow-color');
  }

  const DH_MARQUEE_SPEED = 55; // px per second

  function restartMarquee(slide, loop) {
    const inner = slide && slide.querySelector('[data-dh-marquee]');
    if (!inner) return 0;
    const container = inner.parentElement;
    const distance = inner.scrollWidth + (container ? container.clientWidth : 0);
    const dur = Math.max(4, distance / DH_MARQUEE_SPEED); // seconds — full pass
    inner.style.animation = 'none';
    void inner.offsetWidth; // reflow so it restarts from the start
    inner.style.animation = `dh-cart-marquee ${dur}s linear${loop ? ' infinite' : ''}`;
    return dur * 1000; // ms
  }

  function activateMarquee(root, realIdx) {
    const st = root.__dh;
    if (!st) return;
    const slide = st.reals[realIdx];
    st.activeScrollMs = 0;
    if (slide && slide.classList.contains('is-scroll')) {
      st.activeScrollMs = restartMarquee(slide, false); // one full pass, then advance
    }
  }

  function announceGo(root, p) {
    const st = root.__dh;
    if (!st) return;
    st.p = p;
    st.track.style.transform = `translateX(-${p * 100}%)`;
    const realIdx = (((p - 1) % st.count) + st.count) % st.count;
    st.reals.forEach((s, idx) => s.setAttribute('aria-hidden', idx === realIdx ? 'false' : 'true'));
    setArrowColour(root, st.reals[realIdx]);
    activateMarquee(root, realIdx);
  }

  function announceStep(root, dir) {
    const st = root.__dh;
    if (!st || st.count < 2) return;
    st.track.style.transition = '';
    const p = st.p + dir;
    announceGo(root, p);
    // Wrapped onto a clone → after the slide finishes, snap (no transition) to
    // the matching real slide so the next press keeps going the same way.
    if (p === st.count + 1 || p === 0) {
      const snapTo = p === 0 ? st.count : 1;
      st.track.addEventListener('transitionend', function handler() {
        st.track.style.transition = 'none';
        announceGo(root, snapTo);
        void st.track.offsetWidth; // force reflow
        st.track.style.transition = '';
        st.track.removeEventListener('transitionend', handler);
      }, { once: true });
    }
  }

  // Self-rescheduling autoplay: a scrolling slide waits until its text has
  // finished passing through before advancing; others use the configured speed.
  function announceSchedule(root) {
    window.clearTimeout(root.__dhTimer);
    const base = parseInt(root.getAttribute('data-dh-announce-autoplay'), 10) || 0;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const st = root.__dh;
    if (!base || reduce || !st || st.count < 2) return;
    const wait = Math.max(base, (st.activeScrollMs || 0) + 800);
    root.__dhTimer = window.setTimeout(() => {
      if (!root.__dhPaused) announceStep(root, 1);
      announceSchedule(root);
    }, wait);
  }

  function initAnnounce() {
    const d = drawerEl();
    if (!d) return;
    d.querySelectorAll('[data-dh-announce]').forEach((root) => {
      const track = root.querySelector('[data-dh-announce-track]');
      if (!track) return;
      const layout = root.getAttribute('data-dh-announce-layout') || 'carousel';

      // Clean slate each render (morph re-renders the track without clones).
      track.querySelectorAll('[data-dh-clone]').forEach((c) => c.remove());
      const reals = Array.from(track.querySelectorAll('[data-dh-announce-slide]'));
      if (!reals.length) return;
      const count = reals.length;

      // STACK: show everything, no slider, marquees loop via CSS.
      if (layout === 'stack' || count < 2) {
        window.clearTimeout(root.__dhTimer);
        track.style.transform = '';
        track.style.transition = 'none';
        reals.forEach((s) => {
          s.setAttribute('aria-hidden', 'false');
          if (s.classList.contains('is-scroll')) {
            const inner = s.querySelector('[data-dh-marquee]');
            if (inner) {
              inner.style.animation = ''; // let the CSS marquee drive it
              const c = inner.parentElement;
              const dist = inner.scrollWidth + (c ? c.clientWidth : 0);
              if (dist > 0) inner.style.setProperty('--dh-marquee-dur', Math.max(6, Math.round(dist / 55)) + 's');
            }
          }
        });
        setArrowColour(root, reals[0]);
        root.__dh = { reals, count, track, p: 0 };
        return;
      }

      // CAROUSEL: clone edges for an endless one-direction loop.
      const firstClone = reals[0].cloneNode(true);
      const lastClone = reals[count - 1].cloneNode(true);
      [firstClone, lastClone].forEach((c) => {
        c.setAttribute('data-dh-clone', '');
        c.setAttribute('aria-hidden', 'true');
      });
      track.insertBefore(lastClone, reals[0]);
      track.appendChild(firstClone);

      root.__dh = { reals, count, track, p: 1, activeScrollMs: 0 };

      if (!root.__dhAnnounceBound) {
        root.__dhAnnounceBound = true;
        root.addEventListener('mouseenter', () => { root.__dhPaused = true; });
        root.addEventListener('mouseleave', () => { root.__dhPaused = false; });
        // Swipe support — only horizontal-dominant gestures, so it doesn't
        // hijack vertical scrolling of the drawer.
        let startX = null;
        let startY = null;
        root.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        }, { passive: true });
        root.addEventListener('touchend', (e) => {
          if (startX == null) return;
          const dx = e.changedTouches[0].clientX - startX;
          const dy = e.changedTouches[0].clientY - startY;
          startX = null;
          startY = null;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            announceStep(root, dx < 0 ? 1 : -1);
            announceSchedule(root);
          }
        }, { passive: true });
      }

      track.style.transition = 'none';
      announceGo(root, root.__dh.p);
      void track.offsetWidth;
      track.style.transition = '';

      announceSchedule(root);
    });
  }

  /* ---- Gift message counter + save ------------------------------------- */

  function updateCounter(gift) {
    const input = gift && gift.querySelector(SEL.giftInput);
    const counter = gift && gift.querySelector(SEL.giftCounter);
    if (!input || !counter) return;
    counter.textContent = `${input.value.length}/${input.getAttribute('maxlength') || 0}`;
  }

  function setStatus(gift, message, type) {
    const status = gift && gift.querySelector(SEL.giftStatus);
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', type === 'error');
    status.classList.toggle('is-success', type === 'success');
  }

  async function saveGiftMessage(gift, value) {
    if (!gift) return;
    const successText = gift.getAttribute('data-dh-gift-message-success') || 'Gift message saved';
    setStatus(gift, 'Saving\u2026');
    try {
      const fd = new FormData();
      fd.append('attributes[Gift message]', value);
      // Attribute-only update: no CartLinesUpdate needed (line items unchanged),
      // so this won't collapse the drawer.
      await fetch(`${UPDATE_URL}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      setStatus(gift, successText, 'success');
    } catch (_error) {
      setStatus(gift, 'Could not save gift message. Please try again.', 'error');
    }
  }

  function scheduleSave(gift) {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      const input = gift.querySelector(SEL.giftInput);
      if (input) saveGiftMessage(gift, input.value.trim());
    }, 500);
  }

  /* ---- Auto-remove an ineligible selected promo gift ------------------- */

  function scheduleCleanup() {
    if (cleanupScheduled) return;
    cleanupScheduled = true;
    window.setTimeout(() => {
      cleanupScheduled = false;
      cleanupInvalidGift();
    }, 300);
  }

  function cleanupInvalidGift() {
    return enqueueMutation(async () => {
      const d = drawerEl();
      if (!d) return;
      const invalid = d.querySelector('[data-dh-promo-gift-invalid-selected="true"]');
      const key = invalid && invalid.getAttribute('data-dh-promo-gift-selected-line-key');
      if (!invalid || !key) return;
      try {
        await changeLine(key, 0);
      } catch (_error) {
        /* leave it; will retry on next cart change */
      }
    });
  }

  /* ---- Checkout validation (required gift message) --------------------- */

  function validateGiftMessages() {
    const d = drawerEl();
    if (!d) return true;
    let ok = true;
    d.querySelectorAll(SEL.gift).forEach((gift) => {
      if (gift.getAttribute('data-dh-gift-message-required') !== 'true') return;
      const input = gift.querySelector(SEL.giftInput);
      if (!input) return; // no card selected → nothing to require
      if (input.value.trim()) {
        setStatus(gift, '', '');
        return;
      }
      ok = false;
      const details = gift.querySelector('details[data-dh-details-id]');
      if (details) { details.open = true; markDetailOpen(details); }
      setStatus(gift, gift.getAttribute('data-dh-gift-message-error') || 'Please enter a gift message before checking out.', 'error');
      input.focus({ preventScroll: false });
    });
    return ok;
  }

  /* ---- Event wiring (delegated; survives morphs) ----------------------- */

  function onClick(event) {
    const t = event.target;
    if (!(t instanceof Element)) return;

    // Cart message slider arrows
    const announceArrow = t.closest('[data-dh-announce-prev], [data-dh-announce-next]');
    if (announceArrow) {
      event.preventDefault();
      const root = announceArrow.closest('[data-dh-announce]');
      if (root && root.__dh) {
        announceStep(root, announceArrow.hasAttribute('data-dh-announce-next') ? 1 : -1);
        announceSchedule(root);
      }
      return;
    }

    // Promo gift remove (the hydrated "Added" affordance — same pattern as
    // the greeting card's remove, and for the same reason: keeping Add and
    // Remove on two different tags, not one button whose label/state we'd
    // have to trust the morph to update in place).
    const promoRemove = t.closest(SEL.promoRemove);
    if (promoRemove) {
      event.preventDefault();
      handlePromoRemoveClick(promoRemove);
      return;
    }

    // Promo gift add / change
    const promoBtn = t.closest(SEL.promoBtn);
    if (promoBtn) {
      event.preventDefault();
      if (promoBtn.getAttribute('aria-disabled') === 'true') return;
      handlePromoAction(promoBtn);
      return;
    }

    // Greeting card remove (the "Added" affordance)
    const cardRemove = t.closest(SEL.giftRemove);
    if (cardRemove) {
      event.preventDefault();
      handleCardRemove(cardRemove);
      return;
    }

    // Greeting card add
    const cardAdd = t.closest(SEL.giftAdd);
    if (cardAdd) {
      event.preventDefault();
      if (cardAdd.getAttribute('aria-disabled') === 'true') return;
      handleCardAdd(cardAdd);
      return;
    }

    // Checkout validation
    const checkout = t.closest(SEL.checkout);
    if (checkout && !validateGiftMessages()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  async function handlePromoAction(button) {
    // FIX (2026-09-06): this used to also handle "remove", with the Add and
    // Remove states sharing one <button> whose data-action/text flipped
    // between them. That meant the morph had to update a focused button's
    // text in place — and confirmed live (direct DOM inspection) that the
    // morph library preserves a focused element's text content regardless
    // of render mode, so the label went stale even though every attribute
    // updated correctly. Now Add/Change is a real <button>, and a selected
    // gift renders as a plain span (hydrated into a clickable "Remove" by
    // hydratePromoAdded, below) — same structure as the greeting card's
    // Add-button → Added-span pattern, which never had this problem because
    // crossing tags forces a full node replacement instead of an in-place
    // text patch. This function now only ever runs for add/change.
    const action = button.getAttribute('data-action') || 'add';
    const variantId = button.getAttribute('data-variant-id');
    const promoKey = button.getAttribute('data-promo-key') || '';
    const blockId = button.getAttribute('data-block-id') || '';
    const tierId = button.getAttribute('data-tier-id') || '';
    const giftTitle = button.getAttribute('data-gift-title') || 'Free gift';
    const label = button.textContent.trim();

    button.blur();
    button.setAttribute('aria-disabled', 'true');
    button.textContent = action === 'change' ? 'Changing\u2026' : 'Adding\u2026';
    markDetailOpen(button);

    try {
      await enqueueMutation(async () => {
        if (!variantId) return;
        const cart = await fetchCart();
        const current = (cart.items || []).find(
          (i) => lineProp(i.properties, '_dh_promo_gift_key') === promoKey && lineProp(i.properties, '_dh_promo_gift_block') === blockId
        );
        if (current && String(current.variant_id) === String(variantId)) return; // already selected
        if (current && current.key) await changeLine(current.key, 0); // swap out old tier

        await addLine(variantId, {
          _dh_promo_gift: 'true',
          _dh_promo_gift_key: promoKey,
          _dh_promo_gift_block: blockId,
          _dh_promo_gift_tier: tierId,
          _dh_promo_gift_title: giftTitle,
        });
        recentlyAddedPromoVariants.set(String(variantId), Date.now());
      });
    } catch (error) {
      button.removeAttribute('aria-disabled');
      reportMutationError(`promo gift ${action}`, error);
      flashError(button, label || 'Add');
    }
  }

  async function handlePromoRemoveClick(el) {
    if (el.getAttribute('aria-disabled') === 'true') return;
    // The line key is already server-rendered on the container (see
    // dh-cart-promo-gift-tiers.liquid's data-dh-promo-gift-selected-line-key)
    // — no need to fetch the cart just to look it up again.
    const container = el.closest(SEL.promo);
    const lineKey = container && container.getAttribute('data-dh-promo-gift-selected-line-key');
    const label = el.textContent.trim();
    el.blur();
    el.setAttribute('aria-disabled', 'true');
    el.textContent = 'Removing\u2026';
    try {
      if (lineKey) await enqueueMutation(() => changeLine(lineKey, 0));
    } catch (error) {
      el.removeAttribute('aria-disabled');
      reportMutationError('promo gift remove', error);
      flashError(el, label || 'Remove');
    }
  }

  async function handleCardAdd(button) {
    const variantId = button.getAttribute('data-variant-id');
    const gift = button.closest(SEL.gift);
    if (!variantId) return;
    button.blur(); // see handlePromoAction — same focused-element precaution
    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Adding\u2026';
    try {
      if (gift) markDetailOpen(gift); // keep the row open through the morph
      await enqueueMutation(() => addLine(variantId, { _dh_gift_card_add_on: 'true' }));
      recentlyAddedCardVariants.set(String(variantId), Date.now());
    } catch (error) {
      button.removeAttribute('aria-disabled');
      reportMutationError('greeting card add', error);
      flashError(button, 'Add');
    }
  }

  async function handleCardRemove(el) {
    if (el.getAttribute('aria-disabled') === 'true') return;
    const row = el.closest('[data-dh-gift-add-on]');
    const gift = el.closest(SEL.gift);
    const variantId = row && row.getAttribute('data-variant-id');
    const productId = row && row.getAttribute('data-product-id');
    el.blur(); // see handlePromoAction — same focused-element precaution
    el.setAttribute('aria-disabled', 'true');
    el.textContent = 'Removing\u2026';
    try {
      await enqueueMutation(async () => {
        const cart = await fetchCart();
        const line = (cart.items || []).find((i) => {
          const isAddOn = lineProp(i.properties, '_dh_gift_card_add_on') === 'true';
          const variantMatches = variantId && String(i.variant_id) === String(variantId);
          const productMatches = productId && String(i.product_id) === String(productId);
          return (isAddOn && (!variantId || variantMatches)) || (variantMatches && (!productId || productMatches));
        });
        if (gift) markDetailOpen(gift);
        if (line && line.key) await changeLine(line.key, 0);
      });
    } catch (error) {
      el.removeAttribute('aria-disabled');
      reportMutationError('greeting card remove', error);
      flashError(el, 'Remove');
    }
  }


  function handlePromoVariantSelect(select) {
    const tier = select && select.closest('[data-dh-promo-gift-tier]');
    if (!tier) return;

    const variantId = String(select.value || '');
    if (!variantId) return;

    tier.setAttribute('data-variant-id', variantId);

    const button = tier.querySelector(SEL.promoBtn);
    if (button) button.setAttribute('data-variant-id', variantId);

    const selectedOption = select.options[select.selectedIndex];
    const imageUrl = selectedOption?.getAttribute('data-image-url') || '';

    if (imageUrl) {
      const image = tier.querySelector('.dh-cart-gift-message__add-on-image img');
      if (image instanceof HTMLImageElement) {
        image.src = imageUrl;
        image.removeAttribute('srcset');
      }
    }
  }

  function onChange(event) {
    const select = event.target instanceof Element ? event.target.closest(SEL.promoVariantSelect) : null;
    if (!select) return;
    handlePromoVariantSelect(select);
  }

  function onInput(event) {
    const input = event.target instanceof Element ? event.target.closest(SEL.giftInput) : null;
    if (!input) return;
    const gift = input.closest(SEL.gift);
    updateCounter(gift);
    scheduleSave(gift);
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = event.target instanceof Element ? event.target.closest(SEL.giftRemove) : null;
    if (!el) return;
    event.preventDefault();
    el.click();
  }

  /* ---- Init + keep in sync across native re-renders -------------------- */

  function hydrateAddedCards(root) {
    (root || document).querySelectorAll('.dh-cart-gift-message__add-on-added').forEach((el) => {
      const convertToRemove = () => {
        if (!el.isConnected) return; // a later morph already replaced this node
        el.textContent = 'Remove';
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('data-dh-gift-add-on-remove', '');
        el.setAttribute('aria-label', 'Remove gift card');
        el.classList.add('dh-cart-gift-message__add-on-link', 'dh-cart-gift-message__add-on-remove');
      };

      const row = el.closest('[data-dh-gift-add-on]');
      const variantId = row && row.getAttribute('data-variant-id');
      const addedAt = variantId && recentlyAddedCardVariants.get(variantId);
      const elapsed = addedAt ? Date.now() - addedAt : Infinity;

      if (addedAt && elapsed < ADDED_FLASH_MS) {
        // Freshly added by this client: hold the literal "Added" text (already
        // server-rendered here) for the remainder of the flash window, then
        // convert. Pre-existing cart rows (page load, unrelated re-render)
        // have no entry here and convert instantly, same as before.
        el.textContent = 'Added';
        window.setTimeout(convertToRemove, ADDED_FLASH_MS - elapsed);
      } else {
        if (addedAt) recentlyAddedCardVariants.delete(variantId);
        convertToRemove();
      }
    });
  }

  function hydratePromoAdded(root) {
    (root || document).querySelectorAll('.dh-cart-promo-gifts__added').forEach((el) => {
      const convertToRemove = () => {
        if (!el.isConnected) return; // a later morph already replaced this node
        el.textContent = 'Remove';
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('data-dh-promo-gift-remove', '');
        el.setAttribute('aria-label', el.getAttribute('data-gift-title') ? `Remove free gift ${el.getAttribute('data-gift-title')}` : 'Remove free gift');
        el.classList.add('dh-cart-gift-message__add-on-link', 'dh-cart-gift-message__add-on-remove');
      };

      const tier = el.closest('[data-dh-promo-gift-tier]');
      const variantId = tier && tier.getAttribute('data-variant-id');
      const addedAt = variantId && recentlyAddedPromoVariants.get(variantId);
      const elapsed = addedAt ? Date.now() - addedAt : Infinity;

      if (addedAt && elapsed < ADDED_FLASH_MS) {
        el.textContent = 'Added';
        window.setTimeout(convertToRemove, ADDED_FLASH_MS - elapsed);
      } else {
        if (addedAt) recentlyAddedPromoVariants.delete(variantId);
        convertToRemove();
      }
    });
  }

  function afterRender() {
    const d = drawerEl();
    if (!d) return;
    hydrateAddedCards(d);
    hydratePromoAdded(d);
    reopenDetails();
    initAnnounce();
    d.querySelectorAll(SEL.gift).forEach(updateCounter);
    scheduleCleanup();
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('input', onInput);
    document.addEventListener('change', onChange);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('blur', (e) => {
      const input = e.target instanceof Element ? e.target.closest(SEL.giftInput) : null;
      if (input) saveGiftMessage(input.closest(SEL.gift), input.value.trim());
    }, true);

    // Track native <details> open/close (toggle doesn't bubble → capture phase),
    // and clear the gift message on close when the block is configured to.
    document.addEventListener('toggle', (e) => {
      const details = e.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      const d = drawerEl();
      if (!d || !d.contains(details)) return;
      // Only manage the footer accordion group (our rows + native note/discount).
      if (!details.closest('.cart-actions')) return;

      // Single-open: opening one closes every other open row in the group.
      if (details.open) {
        d.querySelectorAll('.cart-actions details[open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      }

      // Our rows: track open-state for morph persistence + clear-on-close.
      if (details.hasAttribute('data-dh-details-id')) {
        trackDetail(details);
        if (!details.open) {
          const gift = details.closest(SEL.gift);
          if (gift && gift.getAttribute('data-dh-gift-message-clear') === 'true') {
            const input = gift.querySelector(SEL.giftInput);
            if (input && input.value) { input.value = ''; updateCounter(gift); saveGiftMessage(gift, ''); }
          }
        }
      }
    }, true);

    // Our own mutations (above) already call afterRender() directly, right
    // after their own morph. This listener is only for OTHER cart changes —
    // e.g. the native quantity stepper on a regular line item — which morph
    // independently. React to that specific change's own promise instead of
    // guessing a flat delay; double rAF waits for its DOM update to actually
    // paint before we touch anything.
    document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
      if (event.__dhOwnMutation) return; // already rendered + afterRender()'d directly in mutate()
      event.promise
        ?.then(() => {
          requestAnimationFrame(() => requestAnimationFrame(afterRender));
        })
        .catch(() => {});
    });

    afterRender();
    window.__dhCartDrawerReady = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();